import React, { useState, useEffect } from "react";
import { 
  Cpu, 
  Zap, 
  Check, 
  Copy, 
  Activity, 
  Info, 
  Sparkles, 
  Radio, 
  Layers, 
  ExternalLink,
  ShieldCheck,
  Play,
  Square,
  RefreshCw,
  Terminal,
  Sliders,
  Droplets
} from "lucide-react";
import { toast } from "sonner";

export interface ConnectedModule {
  id: string;
  name: string;
  category: "sensor" | "actuator" | "display" | "power" | "rf";
  pinConnections: {
    modulePin: string;
    mcuPin: string;
    wireColor: string; // "#EF4444" | "#1F2937" | "#3B82F6" | etc.
    signalType: "VCC (3.3V/5V)" | "GND" | "I2C SDA" | "I2C SCL" | "ADC Analog" | "GPIO Digital" | "PWM" | "UART TX/RX";
  }[];
  voltage: string;
  status: "active" | "standby" | "alert";
  notes?: string;
}

interface PinoutDiagramProps {
  boardType?: "ESP32-S3 WROOM" | "RP2040 Raspberry Pi Pico" | "Arduino Nano 33 BLE";
  title?: string;
  modules?: ConnectedModule[];
  firmwareCode?: string;
  compact?: boolean;
  /** T9 bridge: a pin chip / table row was clicked (MCU pin label, e.g. "GPIO 21 (I2C SDA)"). */
  onPinClick?: (mcuPin: string) => void;
  /** Flat wiring rows (project / estudio / sample mapping); grouped into modules by targetDevice. Wins over `modules`. */
  connections?: PinConnectionRow[];
}

export type PinConnectionRow = { boardPin: string; targetDevice: string; wireColor: string; notes?: string };

function signalTypeOf(boardPin: string): ConnectedModule["pinConnections"][number]["signalType"] {
  const t = boardPin.toLowerCase();
  if (/^gnd/.test(t)) return "GND";
  if (/^(3v3|3\.3|5v|vin|vcc)/.test(t)) return "VCC (3.3V/5V)";
  if (/sda/.test(t)) return "I2C SDA";
  if (/scl/.test(t)) return "I2C SCL";
  if (/adc/.test(t)) return "ADC Analog";
  if (/pwm/.test(t)) return "PWM";
  if (/\b(tx|rx)\b/.test(t)) return "UART TX/RX";
  return "GPIO Digital";
}

function categoryOf(device: string): ConnectedModule["category"] {
  const t = device.toLowerCase();
  if (/rel[eé]|motor|v[aá]lvula|servo|bomba|actuador/.test(t)) return "actuator";
  if (/buck|bater|bms|fuente|power|5v|12v/.test(t)) return "power";
  if (/oled|lcd|display|pantalla/.test(t)) return "display";
  if (/lora|rf|radio|wifi|ble/.test(t)) return "rf";
  return "sensor";
}

/** Flat rows → one module per target device, keeping row order. */
export function modulesFromConnections(rows: PinConnectionRow[]): ConnectedModule[] {
  const byDevice = new Map<string, ConnectedModule>();
  for (const r of rows) {
    let m = byDevice.get(r.targetDevice);
    if (!m) {
      m = {
        id: `conn-${byDevice.size + 1}`,
        name: r.targetDevice,
        category: categoryOf(r.targetDevice),
        voltage: "—",
        status: "active",
        pinConnections: [],
      };
      byDevice.set(r.targetDevice, m);
    }
    // Module side reads like the board label's signal ("GPIO 21 (SDA)" → "SDA"); route notes go on the module.
    const signal = r.boardPin.match(/\(([^)]+)\)/)?.[1];
    if (r.notes && !(m.notes ?? "").includes(r.notes)) m.notes = m.notes ? `${m.notes} · ${r.notes}` : r.notes;
    m.pinConnections.push({
      modulePin: signal ?? r.boardPin,
      mcuPin: r.boardPin,
      wireColor: r.wireColor,
      signalType: signalTypeOf(r.boardPin),
    });
  }
  return Array.from(byDevice.values());
}

const DEFAULT_MODULES: ConnectedModule[] = [
  {
    id: "mod-miflora",
    name: "Sensor de Suelo Mi Flora (BLE / Analógico)",
    category: "sensor",
    voltage: "3.3V",
    status: "active",
    notes: "Medición capacitiva de humedad y conductividad EC de suelo",
    pinConnections: [
      { modulePin: "VCC", mcuPin: "3V3 (Pin 1)", wireColor: "#EF4444", signalType: "VCC (3.3V/5V)" },
      { modulePin: "GND", mcuPin: "GND (Pin 2)", wireColor: "#1F2937", signalType: "GND" },
      { modulePin: "AOUT", mcuPin: "GPIO 4 (ADC1_CH3)", wireColor: "#10B981", signalType: "ADC Analog" },
    ]
  },
  {
    id: "mod-valve-relay",
    name: "Válvula Solenoide 12V + Módulo Relé Optoacoplado",
    category: "actuator",
    voltage: "12V Ext / 3.3V Logic",
    status: "active",
    notes: "Control de paso de agua para sistema de riego automatizado",
    pinConnections: [
      { modulePin: "IN1 (Trigger)", mcuPin: "GPIO 5 (Digital Output)", wireColor: "#F59E0B", signalType: "GPIO Digital" },
      { modulePin: "VCC", mcuPin: "5V / VIN", wireColor: "#EF4444", signalType: "VCC (3.3V/5V)" },
      { modulePin: "GND", mcuPin: "GND (Pin 14)", wireColor: "#1F2937", signalType: "GND" },
    ]
  },
  {
    id: "mod-oled",
    name: "Pantalla OLED 0.96\" SSD1306 (I2C)",
    category: "display",
    voltage: "3.3V",
    status: "active",
    notes: "Telemetría local en vivo de humedad, batería y estado",
    pinConnections: [
      { modulePin: "SDA", mcuPin: "GPIO 21 (I2C SDA)", wireColor: "#3B82F6", signalType: "I2C SDA" },
      { modulePin: "SCL", mcuPin: "GPIO 22 (I2C SCL)", wireColor: "#EAB308", signalType: "I2C SCL" },
      { modulePin: "VCC", mcuPin: "3V3 (Pin 1)", wireColor: "#EF4444", signalType: "VCC (3.3V/5V)" },
      { modulePin: "GND", mcuPin: "GND (Pin 2)", wireColor: "#1F2937", signalType: "GND" },
    ]
  },
  {
    id: "mod-bms",
    name: "Batería LiFePO4 3.2V / Shunt INA226",
    category: "power",
    voltage: "3.2V - 3.65V",
    status: "active",
    notes: "Monitoreo de corriente, voltaje y porcentaje de carga (SoC)",
    pinConnections: [
      { modulePin: "SDA", mcuPin: "GPIO 21 (I2C SDA)", wireColor: "#3B82F6", signalType: "I2C SDA" },
      { modulePin: "SCL", mcuPin: "GPIO 22 (I2C SCL)", wireColor: "#EAB308", signalType: "I2C SCL" },
    ]
  }
];

const DEFAULT_CODE = `// =========================================================================
// Firmware Prometheus Lab & Aura OS — ESP32-S3 Pinout & Telemetría
// =========================================================================
#include <Wire.h>
#include <Adafruit_GFX.h>
#include <Adafruit_SSD1306.h>

#define PIN_SOIL_ADC     4   // GPIO 4: Entrada analógica de humedad
#define PIN_VALVE_RELAY  5   // GPIO 5: Salida digital de activación de relé
#define I2C_SDA         21   // Bus I2C Datos
#define I2C_SCL         22   // Bus I2C Reloj

#define UMBRAL_RIEGO_PCT 35  // Dispara riego si humedad < 35%

Adafruit_SSD1306 display(128, 64, &Wire, -1);

void setup() {
  Serial.begin(115200);
  pinMode(PIN_SOIL_ADC, INPUT);
  pinMode(PIN_VALVE_RELAY, OUTPUT);
  digitalWrite(PIN_VALVE_RELAY, LOW); // Apagada por defecto

  Wire.begin(I2C_SDA, I2C_SCL);
  if (!display.begin(SSD1306_SWITCHCAPVCC, 0x3C)) {
    Serial.println(F("[ERROR] OLED no detectada en 0x3C"));
  }

  display.clearDisplay();
  display.setTextSize(1);
  display.setTextColor(SSD1306_WHITE);
  display.setCursor(0, 0);
  display.println(F("PROMETHEUS LAB AI"));
  display.println(F("ESP32-S3 ONLINE"));
  display.display();
  delay(1000);
}

void loop() {
  int rawADC = analogRead(PIN_SOIL_ADC);
  // Mapeo inverso de 12 bits (0-4095) a porcentaje (0-100%)
  int moisturePct = map(rawADC, 4095, 1200, 0, 100);
  moisturePct = constrain(moisturePct, 0, 100);

  bool valveActive = false;
  if (moisturePct < UMBRAL_RIEGO_PCT) {
    digitalWrite(PIN_VALVE_RELAY, HIGH);
    valveActive = true;
  } else {
    digitalWrite(PIN_VALVE_RELAY, LOW);
    valveActive = false;
  }

  // Telemetría OLED
  display.clearDisplay();
  display.setCursor(0, 0);
  display.println(F("TELEMETRIA RIEGO"));
  display.drawLine(0, 10, 128, 10, SSD1306_WHITE);
  display.setCursor(0, 16);
  display.printf("Humedad: %d%%\\n", moisturePct);
  display.setCursor(0, 30);
  display.printf("Valvula: %s\\n", valveActive ? "ABIERTA (ON)" : "CERRADA (OFF)");
  display.setCursor(0, 44);
  display.printf("Bateria: 3.34V (94%%)\\n");
  display.display();

  Serial.printf("[TELEMETRY] RawADC: %d | Hum: %d%% | Valve: %d\\n", rawADC, moisturePct, valveActive);
  delay(2000);
}
`;

export const InteractivePinoutDiagram: React.FC<PinoutDiagramProps> = ({
  boardType = "ESP32-S3 WROOM",
  title = "Esquema de Conexión & Asignación de Pines GPIO",
  modules: modulesProp = DEFAULT_MODULES,
  firmwareCode = DEFAULT_CODE,
  compact = false,
  onPinClick,
  connections,
}) => {
  const modules = React.useMemo(
    () => (connections && connections.length > 0 ? modulesFromConnections(connections) : modulesProp),
    [connections, modulesProp]
  );
  const [activeTab, setActiveTab] = useState<"diagram" | "pinout-table" | "firmware" | "simulation">("diagram");
  const [selectedModule, setSelectedModule] = useState<string>(modules[0]?.id || "");
  const [copied, setCopied] = useState(false);

  // Live Simulation state
  const [isSimRunning, setIsSimRunning] = useState(true);
  const [soilMoisture, setSoilMoisture] = useState<number>(42);
  const [manualOverrideValve, setManualOverrideValve] = useState<boolean | null>(null);
  const [serialLogs, setSerialLogs] = useState<string[]>([
    "[SYSTEM] ESP32-S3 bootloader v4.4 OK",
    "[I2C] SSD1306 OLED inicializada en 0x3C",
    "[ADC] Calibración ADC1_CH3 completada (GPIO 4)",
    "[TELEMETRY] Humedad: 42% | Válvula: OFF | Batería: 3.34V"
  ]);

  const isValveActive = manualOverrideValve !== null ? manualOverrideValve : soilMoisture < 35;
  const rawADCValue = Math.round(4095 - (soilMoisture / 100) * (4095 - 1200));

  useEffect(() => {
    if (!isSimRunning) return;
    const interval = setInterval(() => {
      const now = new Date().toLocaleTimeString();
      const logEntry = `[${now}] ADC: ${rawADCValue} | Hum: ${soilMoisture}% | Válvula: ${isValveActive ? "ON (Riego Activo)" : "OFF"}`;
      setSerialLogs((prev) => [logEntry, ...prev.slice(0, 18)]);
    }, 2500);
    return () => clearInterval(interval);
  }, [isSimRunning, soilMoisture, isValveActive, rawADCValue]);

  const copyCode = () => {
    navigator.clipboard.writeText(firmwareCode);
    setCopied(true);
    toast.success("Código de firmware copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const activeModuleData = modules.find((m) => m.id === selectedModule) || modules[0];

  return (
    <div className="w-full bg-card border border-primary/30 rounded-2xl overflow-hidden font-mono-tech shadow-2xl">
      {/* Header Bar */}
      <div className="p-4 bg-void/80 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-primary/10 border border-primary/30 text-primary">
            <Cpu className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h4 className="font-serif-display text-sm sm:text-base font-bold text-foreground">
              {title}
            </h4>
            <span className="text-[10px] text-muted-foreground font-mono">
              Placa Base: <strong className="text-primary">{boardType}</strong> · 3.3V Lógica · Conexiones Validadas
            </span>
          </div>
        </div>

        {/* Action Tabs */}
        <div className="flex items-center gap-1.5 bg-void p-1 rounded-xl border border-border text-xs">
          <button
            onClick={() => setActiveTab("diagram")}
            className={`px-3 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
              activeTab === "diagram" ? "bg-primary text-primary-foreground font-bold shadow" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Activity className="w-3 h-3" />
            <span>1. Esquema</span>
          </button>
          <button
            onClick={() => setActiveTab("pinout-table")}
            className={`px-3 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
              activeTab === "pinout-table" ? "bg-primary text-primary-foreground font-bold shadow" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Layers className="w-3 h-3" />
            <span>2. Mapeo Pines</span>
          </button>
          <button
            onClick={() => setActiveTab("simulation")}
            className={`px-3 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
              activeTab === "simulation" ? "bg-emerald-500 text-white font-bold shadow-[0_0_10px_rgba(16,185,129,0.5)]" : "text-emerald-400 hover:text-emerald-300"
            }`}
          >
            <Play className="w-3 h-3" />
            <span>3. Simulación Viva</span>
          </button>
          <button
            onClick={() => setActiveTab("firmware")}
            className={`px-3 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
              activeTab === "firmware" ? "bg-primary text-primary-foreground font-bold shadow" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Zap className="w-3 h-3" />
            <span>4. C++ Firmware</span>
          </button>
        </div>
      </div>

      {/* TAB 1: INTERACTIVE SVG DIAGRAM */}
      {activeTab === "diagram" && (
        <div className="p-4 sm:p-6 bg-gradient-to-b from-void via-card/50 to-void relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />

          <div className="grid lg:grid-cols-12 gap-6 items-center relative z-10">
            {/* Center Left: Microcontroller Board UI */}
            <div className="lg:col-span-5 flex flex-col items-center">
              <div className="w-full max-w-[280px] p-5 rounded-2xl bg-[#0d131f] border-2 border-primary/40 shadow-[0_0_25px_rgba(56,189,248,0.15)] relative space-y-4">
                <div className="flex items-center justify-between border-b border-border/40 pb-2.5">
                  <span className="text-[10px] text-primary font-bold tracking-wider uppercase flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-400" /> {boardType}
                  </span>
                  <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-ping" />
                </div>

                <div className="w-full h-24 rounded-xl bg-black/60 border border-border/60 flex flex-col items-center justify-center relative p-2 shadow-inner">
                  <div className="text-[11px] font-bold text-foreground/90 font-mono tracking-widest">ESP-WROOM-32</div>
                  <div className="text-[8px] text-muted-foreground font-mono">240MHz · Dual Core · 3.3V</div>
                  <div className="absolute bottom-1 right-2 text-[7px] text-[#39FF14] font-mono">PWR: 3.3V OK</div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[9px] font-mono">
                  <div className="space-y-1.5">
                    <div className="px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/30 text-red-400 font-bold">3V3 (VCC)</div>
                    <div className="px-1.5 py-0.5 rounded bg-gray-500/10 border border-gray-500/30 text-muted-foreground font-bold">GND (Tierra)</div>
                    <div className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold">GPIO 4 (ADC)</div>
                    <div className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 font-bold">GPIO 5 (Out)</div>
                  </div>
                  <div className="space-y-1.5 text-right">
                    <div className="px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-400 font-bold">GPIO 21 (SDA)</div>
                    <div className="px-1.5 py-0.5 rounded bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-bold">GPIO 22 (SCL)</div>
                    <div className="px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/30 text-purple-400 font-bold">TX / RX</div>
                    <div className="px-1.5 py-0.5 rounded bg-gray-500/10 border border-gray-500/30 text-muted-foreground font-bold">GND</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Center Right: Connected Sensor & Actuator Nodes */}
            <div className="lg:col-span-7 space-y-3">
              <div className="flex items-center justify-between text-[10px] text-muted-foreground border-b border-border/40 pb-1.5">
                <span className="uppercase font-bold text-primary">Módulos Conectados ({modules.length}):</span>
                <span>Haz clic para ver cableado</span>
              </div>

              <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
                {modules.map((mod) => {
                  const isSelected = mod.id === selectedModule;
                  return (
                    <div
                      key={mod.id}
                      onClick={() => setSelectedModule(mod.id)}
                      className={`p-3.5 rounded-xl border text-xs cursor-pointer transition-all flex flex-col gap-2 ${
                        isSelected
                          ? "bg-card border-primary shadow-[0_0_15px_rgba(56,189,248,0.2)]"
                          : "bg-void/60 border-border/60 hover:border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${mod.status === "active" ? "bg-[#39FF14]" : "bg-amber-400"}`} />
                          <span className="font-bold text-foreground font-sans text-xs">{mod.name}</span>
                        </div>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-void border border-border/60 font-mono text-primary font-bold">
                          {mod.voltage}
                        </span>
                      </div>

                      {mod.notes && (
                        <p className="text-[10px] text-muted-foreground font-sans leading-tight">
                          {mod.notes}
                        </p>
                      )}

                      {/* Cable routing chips */}
                      <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/30">
                        {mod.pinConnections.map((pin, pIdx) => (
                          <span
                            key={pIdx}
                            role={onPinClick ? "button" : undefined}
                            tabIndex={onPinClick ? 0 : undefined}
                            title={onPinClick ? "Ver este pin en el estudio 3D" : undefined}
                            onClick={
                              onPinClick
                                ? (e) => {
                                    e.stopPropagation();
                                    onPinClick(pin.mcuPin);
                                  }
                                : undefined
                            }
                            className={`text-[8.5px] px-2 py-0.5 rounded-md bg-void/80 border border-border font-mono flex items-center gap-1.5 ${
                              onPinClick ? "cursor-pointer hover:border-primary/60" : ""
                            }`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pin.wireColor }} />
                            <span>{pin.modulePin} ➔ <strong className="text-foreground">{pin.mcuPin}</strong></span>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PINOUT MAPPING TABLE */}
      {activeTab === "pinout-table" && (
        <div className="p-4 sm:p-5 overflow-x-auto space-y-3">
          <div className="text-[10px] text-muted-foreground">
            Tabla de referencia rápida para ensamblaje físico con cautín o protoboard:
          </div>
          <table className="w-full text-left text-xs border-collapse font-sans">
            <thead>
              <tr className="border-b border-border bg-void/60 text-[10px] uppercase font-mono text-primary">
                <th className="p-2.5">Módulo Periférico</th>
                <th className="p-2.5">Pin Módulo</th>
                <th className="p-2.5">Pin Microcontrolador</th>
                <th className="p-2.5">Tipo de Señal</th>
                <th className="p-2.5">Color Cable Recomendado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-[11px]">
              {modules.flatMap((m) =>
                m.pinConnections.map((conn, idx) => (
                  <tr
                    key={`${m.id}-${idx}`}
                    onClick={onPinClick ? () => onPinClick(conn.mcuPin) : undefined}
                    title={onPinClick ? "Ver este pin en el estudio 3D" : undefined}
                    className={`hover:bg-void/40 transition-colors ${onPinClick ? "cursor-pointer" : ""}`}
                  >
                    <td className="p-2.5 font-bold text-foreground">{m.name}</td>
                    <td className="p-2.5 font-mono text-primary">{conn.modulePin}</td>
                    <td className="p-2.5 font-mono font-bold text-foreground">{conn.mcuPin}</td>
                    <td className="p-2.5 text-muted-foreground">{conn.signalType}</td>
                    <td className="p-2.5 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: conn.wireColor }} />
                      <span className="font-mono text-[10px] text-muted-foreground">{conn.wireColor}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 3: LIVE SIMULATION BENCH */}
      {activeTab === "simulation" && (
        <div className="p-4 sm:p-6 bg-gradient-to-b from-[#090d16] to-[#04060a] space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 pb-3">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${isSimRunning ? "bg-[#39FF14] animate-ping" : "bg-red-500"}`} />
              <span className="font-bold text-xs text-foreground uppercase tracking-wider">
                Simulador en Vivo ESP32 & Periféricos
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsSimRunning(!isSimRunning)}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  isSimRunning ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-emerald-500 text-white"
                }`}
              >
                {isSimRunning ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                <span>{isSimRunning ? "Pausar" : "Ejecutar"}</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-4">
            {/* Virtual Hardware Controls */}
            <div className="md:col-span-6 bg-card/60 border border-border/80 p-4 rounded-xl space-y-4">
              <span className="text-[10px] uppercase font-bold text-primary block border-b border-border/40 pb-1">
                Controles de Sensores & Actuadores:
              </span>

              {/* Moisture Slider */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-foreground flex items-center gap-1.5">
                    <Droplets className="w-3.5 h-3.5 text-blue-400" /> Humedad de Suelo (Mi Flora)
                  </span>
                  <span className="font-mono text-primary font-bold text-sm">{soilMoisture}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={soilMoisture}
                  onChange={(e) => {
                    setSoilMoisture(Number(e.target.value));
                    setManualOverrideValve(null);
                  }}
                  className="w-full accent-primary cursor-pointer"
                />
                <div className="flex justify-between text-[9px] text-muted-foreground font-mono">
                  <span>0% (Seco / Umbral Riego)</span>
                  <span className="text-amber-400">Umbral: 35%</span>
                  <span>100% (Saturado)</span>
                </div>
              </div>

              {/* Solenoid Valve State Box */}
              <div className="p-3 rounded-xl border bg-void/80 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-foreground block">
                    Válvula Solenoide 12V (Relé GPIO 5)
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {isValveActive ? "💦 Agua fluyendo hacia las plantas" : "🔒 Circuito cerrado"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold font-mono uppercase ${
                    isValveActive ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 animate-pulse" : "bg-muted/40 text-muted-foreground"
                  }`}>
                    {isValveActive ? "ABIERTA" : "CERRADA"}
                  </span>
                  <button
                    onClick={() => setManualOverrideValve(manualOverrideValve === null ? !isValveActive : null)}
                    className="text-[9px] px-2 py-1 bg-void border border-border hover:border-primary text-primary rounded cursor-pointer"
                  >
                    {manualOverrideValve === null ? "Manual" : "Auto"}
                  </button>
                </div>
              </div>

              {/* Virtual OLED Display */}
              <div className="p-3 bg-black border-2 border-primary/30 rounded-xl space-y-1 font-mono text-center shadow-inner">
                <div className="text-[8px] text-muted-foreground uppercase tracking-widest border-b border-primary/20 pb-0.5">
                  PANTALLA OLED SSD1306 (0x3C)
                </div>
                <div className="py-2 text-left text-[11px] text-[#39FF14] leading-relaxed">
                  <div>┌ TELEMETRIA RIEGO ──────┐</div>
                  <div>│ Humedad: {soilMoisture.toString().padStart(2, " ")}%             │</div>
                  <div>│ Válvula: {isValveActive ? "ON  (ABIERTA) " : "OFF (CERRADA) "} │</div>
                  <div>│ Batería: 3.34V (94%)       │</div>
                  <div>└────────────────────────┘</div>
                </div>
              </div>
            </div>

            {/* Serial Monitor */}
            <div className="md:col-span-6 bg-black/90 border border-border/80 p-4 rounded-xl flex flex-col justify-between space-y-2">
              <div className="flex items-center justify-between border-b border-border/40 pb-1.5 text-[10px] text-muted-foreground font-mono">
                <span className="flex items-center gap-1.5 text-primary font-bold">
                  <Terminal className="w-3.5 h-3.5" /> Monitor Serial @ 115200 baud
                </span>
                <span className="text-[8px] text-emerald-400">COM3 (USB CDC)</span>
              </div>

              <div className="bg-black p-2.5 rounded-lg border border-border/40 font-mono text-[9.5px] text-emerald-400 h-[210px] overflow-y-auto space-y-1">
                {serialLogs.map((log, idx) => (
                  <div key={idx} className="leading-tight select-text">
                    {log}
                  </div>
                ))}
              </div>

              <div className="text-[9px] text-muted-foreground text-right font-mono">
                Lógica en tiempo real sincronizada con el firmware C++
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: FIRMWARE CODE */}
      {activeTab === "firmware" && (
        <div className="p-4 sm:p-5 bg-black/80 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground uppercase font-mono">
              Código Arduino C++ / ESPHome compatible con PlatformIO
            </span>
            <button
              onClick={copyCode}
              className="px-3 py-1 bg-void border border-border hover:border-primary text-foreground text-xs rounded-lg cursor-pointer transition-all flex items-center gap-1.5"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? "¡Copiado!" : "Copiar Firmware"}</span>
            </button>
          </div>

          <pre className="p-4 rounded-xl bg-black border border-border font-mono text-[10.5px] text-emerald-400 overflow-x-auto max-h-[380px] select-text">
            {firmwareCode}
          </pre>
        </div>
      )}
    </div>
  );
};
